"use client"

import { UiIcon } from '@/components/ui/ui-icon'
import Link from "next/link"
import Image from "next/image"
import { ExternalLink, Gamepad2, Heart, HelpCircle, Leaf } from "lucide-react"
import { FooterLawnCTA } from "@/components/ui/footer-lawn-cta"
import { AppStoreBadgeLink, isAppPromoLive } from "@/components/ui/app-download-cta"
import { CcpaOptOutSlot } from "@/components/ui/ccpa-optout-slot"
import { GooglePreferredSourceButton } from "@/components/ui/google-preferred-source-button"
import { BRAND_ASSET_PATHS } from "@/lib/brand-assets"
import { useWeedIdCapability } from "@/hooks/use-weed-id-capability"
import { communityAnalytics, contentAnalytics } from "@/lib/analytics"

type FooterLink = {
  label: string
  mobileLabel?: string
  href: string
  external?: boolean
  affiliate?: boolean
  featured?: boolean
}

const FOOTER_EXPLORE_LINKS: FooterLink[] = [
  { label: "Guides", href: "/guides" },
  { label: "Grass Identification Atlas", mobileLabel: "Grass Atlas", href: "/grass-atlas?source=footer" },
  { label: "Diagnose a Lawn Problem", mobileLabel: "Diagnose", href: "/diagnose" },
  { label: "Community", href: "/community" },
  { label: "Identify a Lawn Weed", mobileLabel: "Weed ID", href: "/weed-identifier?source=footer" },
  { label: "Pricing", href: "/pricing" },
  { label: "Common Weeds", mobileLabel: "Weeds", href: "/weeds" },
  { label: "Common Pests", mobileLabel: "Pests", href: "/pests" },
  { label: "Lawn Care by City", mobileLabel: "By City", href: "/lawn-care" },
  { label: "Lawn Care Directory", mobileLabel: "Directory", href: "/directory" },
  { label: "Blog", href: "/blog" },
  { label: "Lawn Arcade", mobileLabel: "Arcade", href: "/games" },
  { label: "FAQ", href: "/faq" },
  { label: "Products", href: "/products" },
  { label: "2026 Products Guide", mobileLabel: "2026 Guide", href: "/lawn-products-guide" },
]

const FOOTER_TOOL_LINKS: FooterLink[] = [
  { label: "Lawn Size Calculator", mobileLabel: "Lawn Size", href: "/tools/lawn-size-calculator" },
  { label: "Soil Temperature Tracker", mobileLabel: "Soil Temp", href: "/tools/soil-temperature" },
  { label: "Fertilizer Calculator", mobileLabel: "Fertilizer", href: "/tools/fertilizer-calculator" },
  { label: "Watering Schedule Generator", mobileLabel: "Watering", href: "/tools/watering-schedule" },
  { label: "Soil Test Analyzer", href: "/tools/soil-test-analyzer" },
  { label: "View All Tools", href: "/tools", featured: true },
]

const FOOTER_COMPANY_LINKS: FooterLink[] = [
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
  { label: "Partner with us", href: "/partner-with-us" },
  { label: "Custom software builds", mobileLabel: "Custom builds", href: "/studio" },
  { label: "Creator program", mobileLabel: "Creators", href: "/creators" },
  { label: "Chrome Extension", mobileLabel: "Extension", href: "/extension" },
  { label: "Developers & API", mobileLabel: "Developers", href: "/developers" },
  {
    label: "Amazon Shop",
    href: "https://www.amazon.com/shop/whatgrassis0b",
    external: true,
    affiliate: true,
  },
]

const FOOTER_LEGAL_LINKS: FooterLink[] = [
  { label: "Terms", href: "/terms" },
  { label: "Privacy", href: "/privacy" },
  { label: "Refunds", href: "/refund-policy" },
  { label: "Affiliate", href: "/legal" },
]

function FooterSection({
  title,
  links,
}: {
  title: string
  links: FooterLink[]
}) {
  return (
    <div>
      <p className="mb-3 text-xs font-bold uppercase tracking-wider text-green-700">{title}</p>
      <ul className="grid grid-cols-2 gap-x-5 gap-y-0 text-sm md:block md:space-y-0.5">
        {links.map((link) => (
          <li key={link.href}>
            <FooterNavLink link={link} />
          </li>
        ))}
      </ul>
    </div>
  )
}

function FooterNavLink({ link }: { link: FooterLink }) {
  const className = [
    "group flex min-h-[44px] items-center py-2 font-medium transition-all duration-300",
    "text-gray-700 hover:text-green-700",
    "md:-m-3 md:rounded-lg md:p-3 md:hover:bg-white/50",
    link.featured ? "font-semibold text-green-700 hover:text-green-800" : "",
  ]
    .filter(Boolean)
    .join(" ")

  const content = (
    <>
      {link.featured ? <UiIcon icon={Leaf} className="mr-1.5 h-4 w-4 md:mr-2" /> : null}
      <span className="transition-transform duration-300 md:group-hover:translate-x-1">
        <span className="md:hidden">{link.mobileLabel ?? link.label}</span>
        <span className="hidden md:inline">{link.label}</span>
      </span>
      {link.external ? (
        <UiIcon icon={ExternalLink} className="ml-1 h-3 w-3 text-gray-400 transition-colors group-hover:text-green-600" />
      ) : null}
    </>
  )

  if (link.external) {
    // The Amazon Shop exit is the one affiliate link in the footer and it fired
    // nothing until now. Dual-fire: the legacy per-surface event keeps the
    // `footer` placement history, the unified event joins to affiliate CTR
    // queries on surface `site-footer`.
    const handleAffiliateClick = () => {
      contentAnalytics.affiliateLinkClicked('site-footer', link.label, link.href, 'footer')
      contentAnalytics.affiliateProductClicked('site-footer', {
        productName: link.label,
        affiliateLink: link.href,
        placement: 'footer',
      })
    }

    return (
      <a
        href={link.href}
        target="_blank"
        rel={link.affiliate ? "noopener noreferrer nofollow sponsored" : "noopener noreferrer"}
        data-affiliate={link.affiliate ? "true" : undefined}
        onClick={link.affiliate ? handleAffiliateClick : undefined}
        className={className}
      >
        {content}
      </a>
    )
  }

  return (
    <Link
      href={link.href}
      prefetch={false}
      onClick={link.href === "/community"
        ? () => communityAnalytics.galleryCtaClicked({ source: "footer" })
        : undefined}
      className={className}
    >
      {content}
    </Link>
  )
}

// Brand identity column (2a). Logo + wordmark + tagline, plus the existing
// arcade + contact affordances restyled as glass circles (moved here from the
// old bottom bar). No fabricated social links.
function FooterBrand() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2.5">
        <Image
          src={BRAND_ASSET_PATHS.iconDefault}
          alt="What Grass Is This? Logo"
          width={44}
          height={44}
          className="h-11 w-11"
        />
        <span className="font-poppins text-lg font-bold tracking-tight text-green-900">
          What Grass Is This?
        </span>
      </div>
      <p className="max-w-xs text-sm leading-relaxed text-gray-700">
        Identify your grass from a photo and get a 12-month care plan built for your climate.
      </p>
      <div className="flex gap-2.5">
        <Link
          href="/games"
          prefetch={false}
          title="Lawn games"
          className="grid h-11 w-11 place-items-center rounded-full border border-white/70 bg-white/55 text-green-700 transition-colors duration-300 hover:bg-white/80 hover:text-green-800"
          aria-label="Play the lawn games"
        >
          <UiIcon icon={Gamepad2} className="h-4 w-4" />
        </Link>
        <Link
          href="/contact"
          prefetch={false}
          className="grid h-11 w-11 place-items-center rounded-full border border-white/70 bg-white/55 text-green-700 transition-colors duration-300 hover:bg-white/80 hover:text-green-800"
          aria-label="Contact Us"
        >
          <UiIcon icon={HelpCircle} className="h-4 w-4" />
        </Link>
      </div>
      <GooglePreferredSourceButton />
    </div>
  )
}

export function Footer() {
  const { capability } = useWeedIdCapability()
  const exploreLinks = capability.analysisEnabled
    ? FOOTER_EXPLORE_LINKS
    : FOOTER_EXPLORE_LINKS.filter((link) => !link.href.startsWith('/weed-identifier'))
  return (
    <footer
      id="site-footer"
      className="relative w-full overflow-hidden border-t border-white/40"
      style={{
        background:
          "linear-gradient(160deg,#dcfce7 0%,#f0fdf4 35%,#fefce8 70%,#d9f99d 100%)",
      }}
    >
      {/* Decorative lawn-wash blobs — purely visual, kept out of the a11y tree
          and never capturing clicks. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 right-16 h-[420px] w-[420px] rounded-full opacity-50"
        style={{ background: "radial-gradient(circle,#4ade80 0%,rgba(74,222,128,0) 70%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-40 -left-20 h-[440px] w-[440px] rounded-full opacity-40"
        style={{ background: "radial-gradient(circle,#fbbf24 0%,rgba(251,191,36,0) 70%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/3 top-24 hidden h-72 w-72 rounded-full opacity-40 lg:block"
        style={{ background: "radial-gradient(circle,#34d399 0%,rgba(52,211,153,0) 70%)" }}
      />

      <div className="relative content-rail flex flex-col gap-6 py-8">
        {/* 1. CTA row — the peak of the footer: name the action, lead with the cards. */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
          <div className="lg:w-64 lg:shrink-0">
            <p className="font-poppins text-2xl font-bold tracking-tight text-green-900">
              Start with your lawn
            </p>
            <p className="mt-1 text-sm leading-relaxed text-green-800">
              One photo is all it takes to get a plan built for your grass.
            </p>
          </div>
          <div className="lg:flex-1">
            <FooterLawnCTA layout="row" />
          </div>
          {/* Post-launch: the real App Store badge (recognition over recall);
              pre-launch fallback keeps the old /app text link. */}
          <div className="lg:shrink-0 lg:self-center">
            {isAppPromoLive ? (
              <AppStoreBadgeLink placement="site_footer" tone="glass" />
            ) : (
              <Link
                href="/app"
                prefetch={false}
                className="inline-flex min-h-[44px] items-center text-sm font-semibold text-green-700 transition-colors hover:text-green-800"
              >
                Get the iOS app
              </Link>
            )}
          </div>
        </div>

        {/* 2. Frosted link panel — brand + three link columns as one glass material. */}
        <div className="ds-glass grid grid-cols-1 gap-8 rounded-3xl p-6 md:grid-cols-2 md:p-8 lg:grid-cols-[1.4fr_1fr_1fr_1fr] lg:gap-10">
          <FooterBrand />
          <FooterSection title="Explore" links={exploreLinks} />
          <FooterSection title="Tools" links={FOOTER_TOOL_LINKS} />
          <FooterSection title="Company" links={FOOTER_COMPANY_LINKS} />
        </div>

        <div className="ds-glass flex flex-col items-center justify-between gap-3 rounded-3xl px-6 py-4 text-center min-[1400px]:flex-row min-[1400px]:rounded-full min-[1400px]:text-left">
          <p className="text-xs font-medium text-green-800 md:text-sm">
            &copy; <span suppressHydrationWarning>{new Date().getFullYear()}</span> What Grass Is This? All rights
            reserved. Made with <UiIcon icon={Heart} className="inline h-3 w-3 text-green-600 md:h-4 md:w-4" /> for lawn lovers.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1">
            {FOOTER_LEGAL_LINKS.map((link) => (
              <Link
                key={link.href}
                prefetch={false}
                href={link.href}
                className="inline-flex min-h-[44px] items-center text-xs text-green-800 transition-colors hover:text-green-900 md:text-sm"
              >
                {link.label}
              </Link>
            ))}
            {/* Mediavine's CCPA opt-out button, relocated here from its
                vendor-default position below the footer. display:contents on
                the slot, so it lands as a peer of the links above rather than
                nesting a box inside the row. Empty (and gap-free) for visitors
                whose CMP never injects it. */}
            <CcpaOptOutSlot />
          </div>
        </div>
      </div>
    </footer>
  )
}
